import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Button, Modal, ModalHeader } from "reactstrap";
import { MdLocationPin } from "react-icons/md";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { showLoader, hideLoader } from "../../utils/action/Action";
import prev_icon_light from "../../assets/prev_icon_light.svg";
import prev_icon_dark from "../../assets/prev_icon_dark.svg";
import next_icon_light from "../../assets/next_icon_light.svg";
import next_icon_dark from "../../assets/next_icon_dark.svg";
import "../../pages/Home/home.scss";
import "./hometopplace.scss";

const handleDragStart = (e) => e.preventDefault();

function HomeTopPlaces() {
  const [slides, setSlides] = useState([]);
  const [expectedImages, setExpectedImages] = useState(0);
  const [loadedCount, setLoadedCount] = useState(0);
  // const [isOverflowing, setIsOverflowing] = useState(false);

  const [saveImagesForShow, setSaveImagesForShow] = useState({});
  const [showImageModal, setShowImageModal] = useState(false);
  const toggle = () => setShowImageModal((s) => !s);
  const autoplayTimer = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const placesData = useSelector(
    (state) => state?.topPlaceActionReducer?.placeData,
  );

  // Stable autoplay plugin
  const autoplay = useMemo(
    () =>
      Autoplay({
        delay: 3000,
        stopOnInteraction: true,
        stopOnMouseEnter: true,
      }),
    [],
  );

  // Embla setup — rely on built-in loop
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: false,
      slidesToScroll: 1,
      containScroll: "trimSnaps",
    },
    [autoplay],
  );

  const restartAutoplay = useCallback(() => {
    if (autoplayTimer.current) {
      clearTimeout(autoplayTimer.current);
    }

    autoplayTimer.current = setTimeout(() => {
      console.log("autoplay.before()");
      autoplay.play();
      console.log("autoplay.play()");
    }, 5000);
  }, [autoplay]);

  const scrollPrev = useCallback(() => {
    autoplay.stop(); // don’t jump again right after click
    emblaApi?.scrollPrev();
    restartAutoplay();
  }, [emblaApi, autoplay, restartAutoplay]);

  const scrollNext = useCallback(() => {
    autoplay.stop();
    emblaApi?.scrollNext();
    restartAutoplay();
  }, [emblaApi, autoplay, restartAutoplay]);

  const onImgLoad = useCallback(() => setLoadedCount((c) => c + 1), []);

  const typeOfTopVisitedPlaces = useCallback(
    (type) => {
      if (type?.adType === "Image") {
        const image = type.fileContent2 || type.fileContent || null;
        setSaveImagesForShow({ title: type?.title, img: image });
        setShowImageModal(true);
      } else if (type?.adType === "OutsideLink") {
        window?.open(type?.adUrl);
      } else if (type?.adType === "Mkm") {
        navigate(`/category-detail/?MkmId=${type?.mkmId}&cor=${type?.poi}`, {
          state: { from: "topPlaces", categoryName: "Cemeteries" },
        });
      } else if (type?.adType === "Tzadikim") {
        navigate(
          `/category-detail/?tzadikimId=${type?.mkmId}&cor=${type?.poi}`,
          {
            state: { from: "topPlaces", categoryName: "Tzadikim" },
          },
        );
      }
    },
    [navigate],
  );

  // Build slides — NO CLONING
  useEffect(() => {
    dispatch(showLoader());
    setLoadedCount(0);

    try {
      const places = (placesData || []).filter(
        (item) => (item?.title || "").trim() !== "",
      );

      const items = [];
      let imgCount = 0;

      // Real slides
      places.forEach((place, i) => {
        const title = place?.title || "";
        items.push(
          <div className="tp-place_detail" key={`place-${i}-${title}`}>
            <div
              className="tp-bg-blur"
              style={{
                backgroundImage: `url(data:${place?.fileType};base64,${place?.fileContent})`,
              }}
            />
            <img
              src={`data:${place?.fileType};base64,${place?.fileContent}`}
              onClick={() => typeOfTopVisitedPlaces(place)}
              onDragStart={handleDragStart}
              onLoad={onImgLoad}
              alt={title}
            />
            <div className="image_title">
              {places.length === 9 ? (
                <h3>
                  <MdLocationPin size={20} />
                  {title}
                </h3>
              ) : (
                <h3>{title}</h3>
              )}
            </div>
          </div>,
        );
        imgCount += 1;
      });

      setSlides(items);
      setExpectedImages(imgCount);
    } catch (e) {
    } finally {
      dispatch(hideLoader());
    }
  }, [placesData, onImgLoad, dispatch, typeOfTopVisitedPlaces]);

  // Re-measure after slides mount/change
  useEffect(() => {
    if (!emblaApi) return;
    const id = setTimeout(() => {
      emblaApi.reInit();
      // after reInit, check if track actually overflows
      const canScroll = emblaApi.canScrollNext() || emblaApi.canScrollPrev();
      // setIsOverflowing(canScroll);
      if (!canScroll) autoplay.stop();
    }, 0);
    return () => clearTimeout(id);
  }, [emblaApi, slides.length, autoplay]);

  // Re-measure once all images loaded (important for correct widths)
  useEffect(() => {
    if (!emblaApi) return;
    if (expectedImages > 0 && loadedCount >= expectedImages) {
      emblaApi.reInit();
      const canScroll = emblaApi.canScrollNext() || emblaApi.canScrollPrev();
      // setIsOverflowing(canScroll);
      if (!canScroll) autoplay.stop();
    }
  }, [emblaApi, loadedCount, expectedImages, autoplay]);

  const closeBtn = (
    <button
      type="button"
      className="ivm__close"
      aria-label="Close"
      onClick={toggle}
    >
      <AiOutlineCloseCircle style={{ height: "28px", width: "28px" }} />
    </button>
  );

  const isHome = location?.pathname === "/";

  return (
    <div
      style={
        !isHome
          ? {
              background: "linear-gradient(0deg, #FFFCF8 -7.05%, #FFF3E4 100%)",
            }
          : {}
      }
    >
      <div
        className="topplace_section topplace_hero nt-topplace_conatiner"
        style={
          isHome
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // maxWidth: "1510px",
                margin: "0 auto",
              }
            : {
                height: "280px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                marginBottom: !isHome && "50px",
              }
        }
      >
        <div className="tp-wrapper">
          <div
            className="topplace_slider"
            style={{ paddingTop: isHome ? "0px" : "30px" }}
          >
            <div className="tp-embla">
              <div className="tp-embla__viewport" ref={emblaRef}>
                <div className="tp-embla__container">
                  {slides.map((node, idx) => (
                    <div
                      className="tp-embla__slide"
                      key={node.key ?? `slide-${idx}`}
                    >
                      {node}
                    </div>
                  ))}
                </div>
              </div>

              {/* Show controls only if we can actually scroll */}
              {/* {isOverflowing && ( */}
              <>
                <Button
                  className="tp-carousel-btn prev-btn"
                  style={{ borderColor: isHome ? "#ffffff" : "#494949" }}
                  onClick={scrollPrev}
                  aria-label="Previous"
                >
                  <img
                    src={isHome ? prev_icon_light : prev_icon_dark}
                    alt="Previous"
                  />
                </Button>
                <Button
                  className="tp-carousel-btn next-btn"
                  style={{ borderColor: isHome ? "#ffffff" : "#494949" }}
                  onClick={scrollNext}
                  aria-label="Next"
                >
                  <img
                    src={isHome ? next_icon_light : next_icon_dark}
                    alt="Next"
                  />
                </Button>
              </>
              {/* )} */}
            </div>
          </div>

          {saveImagesForShow && Object.keys(saveImagesForShow)?.length > 0 && (
            <Modal
              className="image-viewer-modal modal-dialog-centered"
              size="md"
              isOpen={showImageModal}
              toggle={toggle}
              modalClassName="image-viewer-modal__dialog"
              contentClassName="image-viewer-modal__content"
            >
              <ModalHeader
                className="ivm__header"
                toggle={toggle}
                close={closeBtn}
              >
                <h2 className="ivm__heading">
                  {saveImagesForShow?.title || "Untitled"}
                </h2>
              </ModalHeader>

              <div className="ivm__images ivm__images--single">
                {saveImagesForShow?.img && (
                  <img
                    src={`data:image/png;base64,${saveImagesForShow.img}`}
                    alt={saveImagesForShow?.title || "Preview"}
                    className="ivm__image"
                  />
                )}
              </div>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default HomeTopPlaces;
